import { Box, Divider, Link } from "@mui/material";
import React from "react";
import {
  ArrayField,
  Datagrid,
  FunctionField,
  RaRecord,
  Show,
  SimpleShowLayout,
  TextField,
  useRecordContext,
} from "react-admin";
import { formatDateTimeWithSeconds } from "../../utils/DateTimeUtils";
import { maskPhoneNumber } from "../../utils/PhoneNumberUtils";
import CustomReferenceField from "../Common/CustomReferenceField";
import { ProductType } from "../../enums/ProductType";
import { OneMealService } from "../../services/OneMealService";
import ImagePreviewer from "../Common/ImagePreviewer";
import { Paragraph } from "../../styles/commonStyles";
import ImageCheckVerdict, { summariseImageCheck } from "./ImageCheckVerdict";
import { ImageProcessingResponse } from "../../interfaces/ImageProcessing/ImageProcessingResponse";

const omsService = new OneMealService();

const VendorPayoutMetaShow = () => {
  const record = useRecordContext();
  const [conveyanceProofScreenShots, setConveyanceProofScreenShots] =
    React.useState<any>([]);

  const getReferenceLink = (
    referenceModelName: string,
    referenceId: number,
  ) => {
    switch (referenceModelName) {
      case ProductType.ONE_MEAL_SERVICE:
        return `/#/OneMealService/${referenceId}/show`;
      case ProductType.CHEF_FOR_PARTY:
        return `/#/ChefForParty/${referenceId}/show`;
      case ProductType.TRAIL:
        return `/#/Trials/${referenceId}/show`;
      default:
        return "";
    }
  };

  React.useEffect(() => {
    if (!record) return;
    if (
      record?.referenceModel?.payoutEvent === 'CONVEYANCE' &&
      record?.referenceModel?.referenceModelName ===
        ProductType.ONE_MEAL_SERVICE
    ) {
      omsService
        .getOneMealService(
          parseInt(record?.referenceModel?.referenceId?.toString()),
        )
        .then((res) => {
          if (res && res?.data) {
            setConveyanceProofScreenShots(
              res?.data?.conveyanceProofScreenShots,
            );
          }
        })
        .catch((err) => {});
    }
    // eslint-disable-next-line
  }, [record]);
  return (
    <Show title="Vendor Payout Details">
      <div style={{ display: "grid", gridTemplateColumns: "30% 40% 30%" }}>
        <SimpleShowLayout>
          <TextField source="id" />
          <TextField source="amount" />
          <TextField source="status" label="status" />
          <TextField label="Created By" />
          {record?.createdBy && (
            <Link href={`/#/AdminUsers/${record?.createdBy}/show`}>
              {record?.createdBy}
            </Link>
          )}
          <TextField label="Type" source="type" />
          <TextField label="Reference Id" />
          {record?.referenceModel?.referenceModelName && (
            <Link
              href={getReferenceLink(
                record?.referenceModel?.referenceModelName,
                record?.referenceModel?.referenceId,
              )}
            >
              {record?.referenceModel?.referenceId}
            </Link>
          )}

          {record?.referenceModel?.oneMealServiceId && (
            <Link
              href={`/#/OneMealService/${record?.referenceModel?.oneMealServiceId}/show`}
            >
              {record?.referenceModel?.oneMealServiceId}
            </Link>
          )}
          {record?.referenceModel?.referenceModelName && (
            <FunctionField
              label="Reference Model"
              render={(record: RaRecord) => (
                <div>{record?.referenceModel?.referenceModelName}</div>
              )}
            />
          )}
          {record?.referenceModel?.oneMealServiceId && (
            <TextField label="Reference Model" />
          )}

          {record?.referenceModel?.oneMealServiceId && (
            <Paragraph style={{ marginTop: "0" }}>Chefit</Paragraph>
          )}

          {record?.referenceModel?.trialId && <TextField label="Trial ID" />}
          {record?.referenceModel?.trialId && (
            <Link href={`/#/Trials/${record?.referenceModel?.trialId}/show`}>
              {record?.referenceModel?.trialId}
            </Link>
          )}
          {record?.referenceModel?.trId && <TextField label="TR ID" />}
          {record?.referenceModel?.trId && (
            <Link
              href={`/#/TemporaryReplacement/${record?.referenceModel?.trId}/show`}
            >
              {record?.referenceModel?.trId}
            </Link>
          )}
          <Box sx={{ mt: 1 }}>
            <strong style={{ fontSize: "0.85rem", color: "#666" }}>Payout Comments</strong>
            <ArrayField emptyText="Not Available" source="payoutComments">
              <Datagrid bulkActionButtons={false} sx={{ mt: 1, "& .RaDatagrid-table": { minWidth: "unset" } }}>
                <TextField emptyText="Not Available" source="comment" />
                <TextField emptyText="Not Available" source="commenterEmail" />
                <FunctionField
                  label="Timestamp"
                  render={(record: RaRecord) =>
                    record?.timestamp ? (
                      <span style={{ whiteSpace: "nowrap" }}>
                        {formatDateTimeWithSeconds(record.timestamp)}
                      </span>
                    ) : "Not Available"
                  }
                />
              </Datagrid>
            </ArrayField>
          </Box>
        </SimpleShowLayout>
        <SimpleShowLayout>
          <TextField label="Type Id" source="typeId" />
          <FunctionField label="Created At" render={(record?: RaRecord) => <span style={{ whiteSpace: 'nowrap' }}>{record?.createdAt ? formatDateTimeWithSeconds(record.createdAt) : '-'}</span>} />
          <FunctionField label="Processed At" render={(record?: RaRecord) => <span style={{ whiteSpace: 'nowrap' }}>{record?.processedAt ? formatDateTimeWithSeconds(record.processedAt) : '-'}</span>} />
          <FunctionField label="Approved At" render={(record?: RaRecord) => <span style={{ whiteSpace: 'nowrap' }}>{record?.approvedAt ? formatDateTimeWithSeconds(record.approvedAt) : '-'}</span>} />
          <FunctionField label="Approved By" render={(record?: RaRecord) => record?.approvedBy ? <Link href={`/#/AdminUsers/${record.approvedBy}/show`}>{record.approvedBy}</Link> : '-'} />
          <FunctionField label="Rejected At" render={(record?: RaRecord) => <span style={{ whiteSpace: 'nowrap' }}>{record?.rejectedAt ? formatDateTimeWithSeconds(record.rejectedAt) : '-'}</span>} />
          <FunctionField label="Rejected By" render={(record?: RaRecord) => record?.rejectedBy ? <Link href={`/#/AdminUsers/${record.rejectedBy}/show`}>{record.rejectedBy}</Link> : '-'} />
          <FunctionField label="Earned At" render={(record?: RaRecord) => <span style={{ whiteSpace: 'nowrap' }}>{record?.earnedAt ? formatDateTimeWithSeconds(record.earnedAt) : '-'}</span>} />
          <FunctionField label="Updated At" render={(record?: RaRecord) => <span style={{ whiteSpace: 'nowrap' }}>{record?.updatedAt ? formatDateTimeWithSeconds(record.updatedAt) : '-'}</span>} />
        </SimpleShowLayout>
        <SimpleShowLayout>
          <CustomReferenceField
            label="Cook Id"
            listName={"cooks"}
            source={"cookId"}
          />
          <FunctionField
            label="Cook Name"
            render={(record: RaRecord) => (
              <div>
                {record?.cook?.cookFullProfile?.firstName}{" "}
                {record?.cook?.cookFullProfile?.lastName}
              </div>
            )}
          />
          <TextField source="cook.cookFullProfile.fname" label="Cook Name" />
          <FunctionField label="Cook Mobile" render={(record: any) => maskPhoneNumber(record?.cook?.mobile)} />
          <TextField source="comments" label="comments" />
          <TextField label="Payout Event" source="referenceModel.payoutEvent" />
          {summariseImageCheck(record?.imageProcessing) && (
            <FunctionField
              label="Image Verdict"
              render={(record: RaRecord) => {
                const summary = summariseImageCheck(record?.imageProcessing);
                return (
                  <ImageCheckVerdict
                    verdict={summary?.verdict}
                    reason={summary?.reason}
                    align="left"
                  />
                );
              }}
            />
          )}
        </SimpleShowLayout>
      </div>
      <Divider />
      {record?.referenceModel?.payoutEvent === 'CONVEYANCE' &&
        conveyanceProofScreenShots?.length && (
          <Box display={"flex"} sx={{ margin: "16px", flexWrap: "wrap" }}>
            {conveyanceProofScreenShots?.map((value: any) => (
              <ImagePreviewer value={value} key={value} />
            ))}
          </Box>
        )}
      {record?.imageProcessing?.length > 0 && (
        <Box sx={{ margin: "16px" }}>
          <strong style={{ fontSize: "0.85rem", color: "#666" }}>Image Processing</strong>
          <Box display="flex" sx={{ mt: 1, flexWrap: "wrap", gap: 2 }}>
            {record.imageProcessing.map((item: ImageProcessingResponse) =>
              item?.imageLink ? (
                <Box key={item.id} display="flex" flexDirection="column" alignItems="center" sx={{ gap: 0.5 }}>
                  <ImagePreviewer value={item.imageLink} />
                  {item?.type === "CONVEYANCE" && item?.cookComments && (
                    <Box sx={{ maxWidth: 140, textAlign: "center" }}>
                      <strong style={{ fontSize: "0.75rem", color: "#666" }}>Cook Comment:</strong>
                      <div style={{ fontSize: "0.8rem", color: "#333", wordBreak: "break-word" }}>
                        {item.cookComments}
                      </div>
                    </Box>
                  )}
                  <ImageCheckVerdict verdict={item?.uniformVerdict} reason={item?.uniformReason} />
                </Box>
              ) : null
            )}
          </Box>
        </Box>
      )}
    </Show>
  );
};

export default VendorPayoutMetaShow;
